import { useEffect, useRef, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer, IconButton, Tooltip } from '@mui/material';
// redux
import { useDispatch, useSelector } from 'react-redux';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Iconify from 'src/minimals/components/Iconify';
import Scrollbar from 'src/minimals/Scrollbar';
import ChatAccount from './ChatAccount';
import ChatSearchResults from './ChatSearchResults';
import ChatContactSearch from './ChatContactSearch';
import ChatConversationTabs from './ChatConversationTabs';
import {
  addRecipients,
  resetActiveConversation,
  setCurrentConversation,
} from 'src/redux/slices/chat';
import { selectCurrentChatData } from 'src/selectors/conversationsSelectors';
import { useTranslation } from 'react-i18next';

const SIDEBAR_WIDTH = 320;
const SIDEBAR_COLLAPSE_WIDTH = 96;

export default function ChatSidebar() {
  const theme = useTheme();
  const [openSidebar, setOpenSidebar] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setSearchFocused] = useState(false);
  const { conversations, activeConversationId, contacts } = useSelector(
    selectCurrentChatData
  );
  const isDesktop = useResponsive('up', 'md');
  const displayResults = searchQuery && isSearchFocused;
  const isCollapse = !openSidebar;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isDesktop) {
      return handleCloseSidebar();
    }
    return handleOpenSidebar();
  }, [isDesktop]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!openSidebar) {
      return setSearchFocused(false);
    }
  }, [openSidebar]);

  const handleOpenSidebar = () => {
    setOpenSidebar(true);
  };

  const handleCloseSidebar = () => {
    setOpenSidebar(false);
  };

  const handleToggleSidebar = () => {
    setOpenSidebar((prev) => !prev);
  };

  const handleClickAwaySearch = () => {
    setSearchFocused(false);
    setSearchQuery('');
  };

  const handleChangeSearch = async (event) => {
    try {
      const { value } = event.target;
      setSearchQuery(value);
      if (value) {
        setSearchResults(
          contacts.filter((user) =>
            user.name.toLowerCase().includes(value.toLowerCase())
          )
        );
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearchFocus = () => {
    setSearchFocused(true);
  };

  const handleSearchSelect = (user) => {
    const previousConversation = conversations.find(
      (conversation) =>
        conversation.type === 'ONE_TO_ONE' &&
        conversation.participants.some(
          (participant) => participant.id === user.id
        )
    );

    if (previousConversation) {
      dispatch(setCurrentConversation(previousConversation.id));
    } else {
      dispatch(resetActiveConversation());
      dispatch(addRecipients([user]));
    }

    setSearchFocused(false);
    setSearchQuery('');
  };

  const handleSelectContact = (result) => {
    if (handleSearchSelect) {
      handleSearchSelect(result);
    }
  };

  const hadActiveConversation = useRef(false);

  useEffect(() => {
    if (activeConversationId) {
      hadActiveConversation.current = true;
      return;
    }

    if (hadActiveConversation.current && !isDesktop) {
      setOpenSidebar(true);
      hadActiveConversation.current = false;
    }
  }, [activeConversationId, isDesktop]);

  const renderContent = (
    <>
      <Box sx={{ py: 2, px: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="center">
          {!isCollapse && (
            <>
              <ChatAccount />
              <Box sx={{ flexGrow: 1 }} />
            </>
          )}
          <IconButton onClick={handleToggleSidebar}>
            <Iconify
              width={20}
              height={20}
              icon={
                openSidebar
                  ? 'eva:arrow-ios-back-fill'
                  : 'eva:arrow-ios-forward-fill'
              }
            />
          </IconButton>
          {!isCollapse && (
            <Tooltip title={t('chat.chat_compose_tooltip')}>
              <IconButton
                onClick={() => {
                  dispatch(resetActiveConversation());
                  if (!isDesktop) {
                    handleCloseSidebar();
                  }
                }}
              >
                <Iconify icon={'eva:edit-fill'} width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {!isCollapse && (
          <ChatContactSearch
            query={searchQuery}
            onFocus={handleSearchFocus}
            onChange={handleChangeSearch}
            onClickAway={handleClickAwaySearch}
          />
        )}
      </Box>
      <Scrollbar>
        {!displayResults ? (
          <ChatConversationTabs
            conversations={conversations}
            isOpenSidebar={openSidebar}
            activeConversationId={activeConversationId}
            onSelectConversation={handleCloseSidebar}
            sx={{ ...(isSearchFocused && { display: 'none' }) }}
          />
        ) : (
          <ChatSearchResults
            query={searchQuery}
            results={searchResults}
            onSelectContact={handleSelectContact}
          />
        )}
      </Scrollbar>
    </>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          open={openSidebar}
          variant="persistent"
          sx={{
            width: SIDEBAR_WIDTH,
            transition: theme.transitions.create('width'),
            '& .MuiDrawer-paper': {
              position: 'static',
              width: SIDEBAR_WIDTH,
            },
            ...(isCollapse && {
              width: SIDEBAR_COLLAPSE_WIDTH,
              '& .MuiDrawer-paper': {
                width: SIDEBAR_COLLAPSE_WIDTH,
                position: 'static',
                transform: 'none !important',
                visibility: 'visible !important',
              },
            }),
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openSidebar}
          variant="persistent"
          sx={{
            width: SIDEBAR_WIDTH,
            height: '100%',
            position: 'absolute',
            zIndex: 10000,
            '& .MuiDrawer-paper': {
              position: 'absolute',
              width: SIDEBAR_WIDTH,
            },
            ...(isCollapse && {
              width: SIDEBAR_COLLAPSE_WIDTH,
              position: 'relative',
              '& .MuiDrawer-paper': {
                width: SIDEBAR_COLLAPSE_WIDTH,
                position: 'absolute',
                transform: 'none !important',
                visibility: 'visible !important',
              },
            }),
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </>
  );
}
